import { useEffect, type RefObject } from 'react';
import type { ChatDialogReadBoundary } from '@/entities/ChatDialog/model/types';

interface UseChatDialogReadOptions {
    boundary: ChatDialogReadBoundary | null;
    enabled: boolean;
    targetRef: RefObject<HTMLElement>;
    viewportRef: RefObject<HTMLElement>;
    onRead: (messageId: string) => Promise<void>;
}

/** Подтверждает прочтение видимого конца показанной переписки */
export const useChatDialogRead = (
    {
        boundary,
        enabled,
        targetRef,
        viewportRef,
        onRead,
    }: UseChatDialogReadOptions
): void => {
    const messageId = boundary?.messageId;

    useEffect(() => {
        const target = targetRef.current;
        const viewport = viewportRef.current;

        if (!enabled || !messageId || !target || !viewport) return;

        /** Подтверждает последнее показанное сообщение в открытой вкладке */
        const confirmVisible = (): void => {
            if (document.visibilityState !== 'visible' || !document.hasFocus()) return;
            const targetBounds = target.getBoundingClientRect();
            const viewportBounds = viewport.getBoundingClientRect();

            const hasVisibleArea = targetBounds.height > 0 && targetBounds.width > 0
                && viewportBounds.height > 0 && viewportBounds.width > 0;

            const visible = hasVisibleArea
                && targetBounds.top >= Math.max(0, viewportBounds.top)
                && targetBounds.bottom <= Math.min(window.innerHeight, viewportBounds.bottom)
                && targetBounds.left >= Math.max(0, viewportBounds.left)
                && targetBounds.right <= Math.min(window.innerWidth, viewportBounds.right);

            if (visible) void onRead(messageId);
        };

        const observer = typeof IntersectionObserver === 'undefined'
            ? null
            : new IntersectionObserver(
                (entries) => {
                    if (entries.some((entry) => entry.isIntersecting)) confirmVisible();
                },
                {
                    root: viewport,
                    threshold: 1
                },
            );

        observer?.observe(target);
        document.addEventListener('visibilitychange', confirmVisible);
        window.addEventListener('focus', confirmVisible);

        window.addEventListener('scroll', confirmVisible, {
            capture: true,
            passive: true
        });

        window.addEventListener('resize', confirmVisible);
        confirmVisible();

        return () => {
            observer?.disconnect();
            document.removeEventListener('visibilitychange', confirmVisible);
            window.removeEventListener('focus', confirmVisible);
            window.removeEventListener('scroll', confirmVisible, true);
            window.removeEventListener('resize', confirmVisible);
        };
    }, [enabled, messageId, onRead, targetRef, viewportRef]);
};
